import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { Card, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { ArrowLeft, Bell, ToggleRight, FileText, Settings, Cog, Monitor, Activity, Brain, Zap, Mail, Wrench, ClipboardList, ClipboardCheck, ClipboardPen, Coins, History, Search, X, AlertTriangle, LifeBuoy } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { CAPS, hasCapability } from '../../utils/capabilities';

// Category identifiers. Each tool declares which category it belongs to.
// The 'all' tab is a synthetic view that shows every visible tool grouped
// under the same category headers.
const CATEGORIES = [
  { key: 'all',       label: 'All' },
  { key: 'config',    label: 'Configuration' },
  { key: 'comms',     label: 'Communications & Users' },
  { key: 'logs',      label: 'Logs & Audit' },
  { key: 'ops',       label: 'Operations' },
];

const AdminToolsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isFullAdmin = user?.role === 'admin';
  const [activeCat, setActiveCat] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const searchTerm = searchQuery.trim().toLowerCase();

  // Phase 4f: each tool declares the capability that unlocks it. Full admins
  // (`role === 'admin'`) always see everything; sub-admins see only the tools
  // whose `cap` they hold. Tools with `adminOnly: true` are hidden from every
  // sub-admin regardless of caps — they're maintenance/infra surfaces.
  const tools = [
    // === Configuration ===
    { title: 'Site Settings', description: 'Configure analytics, integrations, and site-wide settings',
      icon: Cog, path: '/admin/tools/settings', color: 'text-orange-600', bgColor: 'bg-orange-100',
      adminOnly: true, category: 'config' },
    { title: 'Module Activation', description: 'Toggle app modules as Active, Coming Soon, or Disabled',
      icon: ToggleRight, path: '/admin/tools/modules', color: 'text-green-600', bgColor: 'bg-green-100',
      adminOnly: true, category: 'config' },
    { title: 'Coin Rewards Settings', description: 'Tune all coin knobs live — value, earning caps, abuse thresholds, and redemption limits. Applies to new transactions instantly.',
      icon: Coins, path: '/admin/tools/coin-settings', color: 'text-yellow-600', bgColor: 'bg-yellow-100',
      adminOnly: true, category: 'config' },
    { title: 'Review Settings', description: 'Manage "Needs Review" categories (Claude, SME Review, etc.) and control whether sub-admins with content access can use the review workflow.',
      icon: ClipboardPen, path: '/admin/tools/review-settings', color: 'text-amber-600', bgColor: 'bg-amber-100',
      adminOnly: true, category: 'config' },
    { title: 'Email Service', description: 'Configure Emailit API for transactional emails and manage templates',
      icon: Mail, path: '/admin/tools/email', color: 'text-teal-600', bgColor: 'bg-teal-100',
      adminOnly: true, category: 'config' },
    { title: 'AI Service Connection', description: 'Configure central AI service for Deep Search',
      icon: Zap, path: '/admin/tools/ai-service', color: 'text-violet-600', bgColor: 'bg-violet-100',
      adminOnly: true, category: 'config' },
    { title: 'Semantic Embeddings', description: 'Manage embeddings, search tuning, abbreviations, and model selection',
      icon: Brain, path: '/admin/tools/embeddings', color: 'text-teal-600', bgColor: 'bg-teal-100',
      adminOnly: true, category: 'config' },

    // === Communications & Users ===
    { title: 'Updates', description: 'Send announcements and notifications to users',
      icon: Bell, path: '/admin/updates', color: 'text-blue-600', bgColor: 'bg-blue-100',
      cap: CAPS.MANAGE_UPDATES, category: 'comms' },
    { title: 'Session Management', description: 'View active sessions and force-logout users',
      icon: Monitor, path: '/admin/tools/sessions', color: 'text-purple-600', bgColor: 'bg-purple-100',
      cap: CAPS.VIEW_SESSIONS, category: 'comms' },
    { title: 'Manual Coin Credit', description: 'Award coins to any user by INDoS — for written-paper submissions, one-off gifts or corrections. Audit-logged and reversible within 24h.',
      icon: Coins, path: '/admin/tools/coin-credits', color: 'text-amber-600', bgColor: 'bg-amber-100',
      cap: CAPS.GRANT_COINS, category: 'comms' },

    // === Logs & Audit ===
    { title: 'Audit Logs', description: 'View login activity and security events',
      icon: FileText, path: '/admin/tools/audit-logs', color: 'text-indigo-600', bgColor: 'bg-indigo-100',
      adminOnly: true, category: 'logs' },
    { title: 'User Activity Log', description: 'Trace every plan change, permission grant, and admin action against any user. Filter by target/actor/date with CSV export.',
      icon: History, path: '/admin/tools/user-activity', color: 'text-sky-600', bgColor: 'bg-sky-100',
      adminOnly: true, category: 'logs' },
    { title: 'Notification Log', description: 'Audit every email + in-app notification the platform has fired. 90-day rolling window with filters and CSV export.',
      icon: ClipboardList, path: '/admin/tools/notification-logs', color: 'text-cyan-600', bgColor: 'bg-cyan-100',
      adminOnly: true, category: 'logs' },
    { title: 'Contribution Report', description: 'Per-admin count of questions marked answered — for payroll and audit. Bifurcated by Orals/Written with time filters and CSV export.',
      icon: ClipboardCheck, path: '/admin/tools/contributions', color: 'text-teal-600', bgColor: 'bg-teal-100',
      adminOnly: true, category: 'logs' },
    { title: 'Client Errors', description: 'Browse React crashes and 5xx failures the users hit, grouped by frequency. Auto-purges after 30 days. Grep by Error ID from a support ticket to jump straight to the stack trace.',
      icon: AlertTriangle, path: '/admin/tools/client-errors', color: 'text-rose-600', bgColor: 'bg-rose-100',
      adminOnly: true, category: 'logs' },

    // === Operations ===
    { title: 'Docs Manager', description: 'Author and permission-gate internal how-to guides for admins and sub-admins. Control article visibility by capability.',
      icon: LifeBuoy, path: '/admin/tools/docs-manager', color: 'text-emerald-600', bgColor: 'bg-emerald-100',
      adminOnly: true, category: 'ops' },
    { title: 'Health Monitoring', description: 'Monitor system health, performance metrics, and errors',
      icon: Activity, path: '/admin/tools/health', color: 'text-red-600', bgColor: 'bg-red-100',
      adminOnly: true, category: 'ops' },
    { title: 'Data Maintenance', description: 'One-off jobs to repair or normalize legacy data (history sentinels, undated entries)',
      icon: Wrench, path: '/admin/tools/data-maintenance', color: 'text-rose-600', bgColor: 'bg-rose-100',
      adminOnly: true, category: 'ops' },
  ];

  const visibleTools = useMemo(() => tools.filter((t) => {
    if (isFullAdmin) return true;
    if (t.adminOnly) return false;
    return t.cap ? hasCapability(user, t.cap) : false;
  }), [tools, isFullAdmin, user]);

  // Only surface tabs the current viewer actually has tools inside.
  const visibleCategories = useMemo(() => {
    const present = new Set(visibleTools.map((t) => t.category));
    return CATEGORIES.filter((c) => c.key === 'all' || present.has(c.key));
  }, [visibleTools]);

  // Hide-not-gate: if a sub-admin lands here via direct URL with no tool
  // caps at all, redirect them back to the admin dashboard rather than
  // rendering a "nothing for you here" empty state.
  useEffect(() => {
    if (user && !isFullAdmin && visibleTools.length === 0) {
      navigate('/admin', { replace: true });
    }
  }, [user, isFullAdmin, visibleTools.length, navigate]);

  const renderToolCard = (tool) => (
    <Card
      key={tool.path}
      onClick={() => navigate(tool.path)}
      className="cursor-pointer hover-lift border-2 border-gray-200 hover:border-[#6366F1] transition-all"
      data-testid={`admin-tool-tile-${tool.path.split('/').pop()}`}
    >
      <CardHeader>
        <div className={`p-3 ${tool.bgColor} rounded-lg mb-4 w-fit`}>
          <tool.icon className={`w-8 h-8 ${tool.color}`} />
        </div>
        <CardTitle className="text-xl">{tool.title}</CardTitle>
        <CardDescription className="text-sm mt-2">
          {tool.description}
        </CardDescription>
      </CardHeader>
    </Card>
  );

  const renderGrid = (toolList) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {toolList.map(renderToolCard)}
    </div>
  );

  // For the "All" view: show grouped by category with a section header
  // above each. For every other tab: show the flat filtered grid.
  const renderAll = () => (
    <div className="space-y-10">
      {CATEGORIES.filter((c) => c.key !== 'all').map((cat) => {
        const toolsInCat = visibleTools.filter((t) => t.category === cat.key);
        if (toolsInCat.length === 0) return null;
        return (
          <section key={cat.key} data-testid={`admin-tools-section-${cat.key}`}>
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-lg font-semibold text-slate-800">{cat.label}</h2>
              <span className="text-xs text-slate-400">{toolsInCat.length} tool{toolsInCat.length === 1 ? '' : 's'}</span>
              <div className="flex-1 h-px bg-gradient-to-r from-slate-200 to-transparent" />
            </div>
            {renderGrid(toolsInCat)}
          </section>
        );
      })}
    </div>
  );

  return (
    <Layout>
      <div className="page-transition">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate('/admin')} className="text-gray-600">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin
          </Button>
        </div>

        <Card className="mb-8 border-l-4 border-l-[#6366F1] shadow-lg">
          <CardHeader className="bg-gradient-to-r from-[#6366F1]/5 to-[#0A7EA4]/5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-[#6366F1] rounded-lg">
                <Settings className="w-8 h-8 text-white" />
              </div>
              <div>
                <CardTitle className="text-3xl text-[#6366F1]">Admin Tools</CardTitle>
                <CardDescription className="text-base mt-1">
                  {isFullAdmin ? 'System configuration and monitoring' : 'The tools your admin has granted you access to'}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        {visibleTools.length === 0 ? null : (() => {
          // Search short-circuits the tab UI: when there's a query, show a
          // flat "search results" grid across every category so admins never
          // have to remember which tab hosts "Semantic Embeddings".
          const matches = searchTerm
            ? visibleTools.filter((t) =>
                t.title.toLowerCase().includes(searchTerm) ||
                (t.description || '').toLowerCase().includes(searchTerm)
              )
            : null;

          return (
            <>
              {/* Search bar */}
              <div className="relative mb-4 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <Input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search tools by name or description…"
                  className="pl-9 pr-9 h-10 text-sm"
                  data-testid="admin-tools-search"
                  aria-label="Search admin tools"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100"
                    aria-label="Clear search"
                    data-testid="admin-tools-search-clear"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                )}
              </div>

              {matches !== null ? (
                <div>
                  <div className="flex items-center gap-3 mb-4 text-sm">
                    <span className="text-slate-700">
                      <strong>{matches.length}</strong> match{matches.length === 1 ? '' : 'es'} for &ldquo;{searchQuery}&rdquo;
                    </span>
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Clear search
                    </button>
                  </div>
                  {matches.length === 0 ? (
                    <div className="text-sm text-slate-500 italic py-8 text-center border border-dashed rounded-lg">
                      No tools match your search. Try a different keyword.
                    </div>
                  ) : (
                    renderGrid(matches)
                  )}
                </div>
              ) : (
                <Tabs value={activeCat} onValueChange={setActiveCat} className="w-full">
                  <TabsList className="mb-6 flex-wrap h-auto" data-testid="admin-tools-tabs">
                    {visibleCategories.map((cat) => (
                      <TabsTrigger
                        key={cat.key}
                        value={cat.key}
                        data-testid={`admin-tools-tab-${cat.key}`}
                      >
                        {cat.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>

                  <TabsContent value="all" className="mt-0">
                    {renderAll()}
                  </TabsContent>

                  {CATEGORIES.filter((c) => c.key !== 'all').map((cat) => (
                    <TabsContent key={cat.key} value={cat.key} className="mt-0">
                      {renderGrid(visibleTools.filter((t) => t.category === cat.key))}
                    </TabsContent>
                  ))}
                </Tabs>
              )}
            </>
          );
        })()}
      </div>
    </Layout>
  );
};

export default AdminToolsPage;
